/**
 * Persistence for the landing page's "start fresh" reset — the × on the pinned
 * space chip. The pin itself is derived from durable sources (the backend's
 * lastSpaceSlug, localStorage, the slug cookie), so a transient component-state
 * dismissal would re-pin on the next page load and quietly route landing
 * submits back into the old space. This flag makes the dismissal equally
 * durable: while set, `useMarketingSession` suppresses the pinned space and the
 * landing behaves like a first visit (submits provision a brand-new space).
 *
 * The flag clears itself the next time the user demonstrably picks a space
 * again — opening one in the app (project-context) or provisioning one from
 * the landing (use-guest-provisioning) — so the pin convenience returns on its
 * own; nothing ever needs to "re-enable" it manually.
 */

const DISMISS_KEY = 'naumu-landing-pin-dismissed';

/**
 * Fired on `window` when the pin is dismissed, so every mounted
 * `useMarketingSession` instance (the hero AND the bottom CTA render their own)
 * un-pins in the same page-load instead of only the composer whose × was
 * clicked.
 */
export const LANDING_PIN_DISMISS_EVENT = 'naumu:landing-pin-dismissed';

export const isLandingSpacePinDismissed = (): boolean => {
	try {
		return localStorage.getItem(DISMISS_KEY) === '1';
	} catch {
		return false;
	}
};

export const dismissLandingSpacePin = (): void => {
	try {
		localStorage.setItem(DISMISS_KEY, '1');
	} catch {
		// Storage unavailable (private mode) — the event still un-pins this
		// page-load; the dismissal just won't survive a reload.
	}
	window.dispatchEvent(new Event(LANDING_PIN_DISMISS_EVENT));
};

export const clearLandingSpacePinDismissal = (): void => {
	try {
		localStorage.removeItem(DISMISS_KEY);
	} catch {
		// Nothing to clear if storage is unavailable.
	}
};
